// src/components/Header.tsx
import React, { useState, useEffect, useRef } from "react";
import Logo from "../../../assets/logo.png";
import { NavIcon } from "../../utils/constants";
import { FullScreenMenu } from "./FullScreenMenu";
import "./Header.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getRoutePaths, switchLangPath, useLang } from "../../utils/i18n";


const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const mobileLangDropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const lang = useLang();
  const paths = getRoutePaths(lang);
  const changeLanguage = (nextLang) => {
    if (nextLang === lang) {
      setMobileLangOpen(false);
      return;
    }
    navigate(switchLangPath(location.pathname, nextLang));
    setMobileLangOpen(false);
  };

  // Lock scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }
    return () => document.body.classList.remove("menu-open");
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  useEffect(() => {
    const SCROLL_THRESHOLD = 18;

    const getScrollTop = () =>
      window.pageYOffset ??
      document.documentElement.scrollTop ??
      document.body.scrollTop ??
      0;

    const updateHeaderState = () => {
      const nextScrolled = getScrollTop() > SCROLL_THRESHOLD;
      setIsScrolled((prev) => (prev === nextScrolled ? prev : nextScrolled));
    };

    updateHeaderState();
    window.addEventListener("scroll", updateHeaderState, { passive: true });
    window.addEventListener("resize", updateHeaderState);
    window.visualViewport?.addEventListener("scroll", updateHeaderState, { passive: true });
    window.visualViewport?.addEventListener("resize", updateHeaderState);

    return () => {
      window.removeEventListener("scroll", updateHeaderState);
      window.removeEventListener("resize", updateHeaderState);
      window.visualViewport?.removeEventListener("scroll", updateHeaderState);
      window.visualViewport?.removeEventListener("resize", updateHeaderState);
    };
  }, []);

  useEffect(() => {
    if (!mobileLangOpen) {
      return;
    }

    const handlePointerDown = (event) => {
      if (
        mobileLangDropdownRef.current &&
        !mobileLangDropdownRef.current.contains(event.target)
      ) {
        setMobileLangOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileLangOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileLangOpen]);

  useEffect(() => {
    setMobileLangOpen(false);
  }, [location.pathname]);


  const toggleMenu = () => {
    setMobileLangOpen(false);
    setMenuOpen((prev) => !prev);
  };
  const headerClassName = `header-main${isScrolled ? " is-scrolled" : " is-top"}${menuOpen ? " menu-is-open" : ""}`;

  return (
    <header className="header">
      <div className={headerClassName}>
        <div className="header-main-inner">
          <div className="header-left">
            <Link
              className="logo-container"
              to={paths.home}
              aria-label="Go to home"
              onClick={() => setMenuOpen(false)}
            >
              <img
                src={Logo}
                className="logo"
                alt="Monetique"
              />
            </Link>
          </div>

          <div className="header-right">
            {/* Hamburger Icon – works on ALL devices */}
            <Link
              className="contact-button desktop-only"
              to={paths.contact}
            >
              {lang === "en" ? "Contact" : "Contato"}
            </Link>
            <div className="lang-toggle" role="group" aria-label="Language">
              <button
                className={`lang-option ${lang === "en" ? "active" : ""}`}
                type="button"
                onClick={() => changeLanguage("en")}
              >
                EN
              </button>
              <span className="lang-separator">|</span>
              <button
                className={`lang-option ${lang === "pt" ? "active" : ""}`}
                type="button"
                onClick={() => changeLanguage("pt")}
              >
                PT
              </button>
            </div>
            <div className="mobile-lang-dropdown" ref={mobileLangDropdownRef}>
              <button
                type="button"
                className="mobile-lang-trigger"
                aria-haspopup="listbox"
                aria-expanded={mobileLangOpen}
                aria-label={lang === "en" ? "Select language" : "Selecionar idioma"}
                onClick={() => setMobileLangOpen((prev) => !prev)}
              >
                <span className="mobile-lang-code">{lang.toUpperCase()}</span>
                <span className={`mobile-lang-caret${mobileLangOpen ? " is-open" : ""}`} />
              </button>
              <div
                className={`mobile-lang-menu${mobileLangOpen ? " is-open" : ""}`}
                role="listbox"
                aria-label={lang === "en" ? "Available languages" : "Idiomas disponiveis"}
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={lang === "en"}
                  className={`mobile-lang-item${lang === "en" ? " active" : ""}`}
                  onClick={() => changeLanguage("en")}
                >
                  EN
                </button>
                <button
                  type="button"
                  role="option"
                  aria-selected={lang === "pt"}
                  className={`mobile-lang-item${lang === "pt" ? " active" : ""}`}
                  onClick={() => changeLanguage("pt")}
                >
                  PT
                </button>
              </div>
            </div>
            <button
              type="button"
              className="menu-trigger"
              onClick={toggleMenu}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="site-menu"
            >
              <NavIcon />
            </button>
          </div>
        </div>
      </div>

      {/* Full-Screen Menu – appears on click, any screen size */}
      <FullScreenMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
    </header>
  );
};

export default Header;
